// Pure helpers for the Project Datasheet Composer's Catalog tab (browse a source, pull into this
// database). No React, no API.

// The server's PullState names → what the tab says about them.
export const STATE_LABEL = {
  New: 'New',
  Identical: 'Already here',
  FieldsDiffer: 'Already here — fields differ',
  DatasheetDiffers: 'Already here — datasheet differs',
  Blocked: 'Blocked',
};

// State → badge colour. One helper per page (ui-standards); unknown values fall back to gray.
const STATE_BADGE = {
  New: 'stui-badge-blue',
  Identical: 'stui-badge-green',
  FieldsDiffer: 'stui-badge-amber',
  DatasheetDiffers: 'stui-badge-orange',
  Blocked: 'stui-badge-red',
};
export const stateBadgeClass = state => STATE_BADGE[state] ?? 'stui-badge-gray';
export const stateLabel = state => STATE_LABEL[state] ?? String(state ?? '');

export const FILTERS = [
  ['all', 'All'],
  ['new', 'New'],
  ['here', 'Already here'],
  ['differs', 'Differs'],
  ['blocked', 'Blocked'],
];

export function filterRows(rows, filter) {
  const list = rows ?? [];
  switch (filter) {
    case 'new': return list.filter(r => r.state === 'New');
    case 'here': return list.filter(r => r.targetId != null);
    case 'differs': return list.filter(r => r.state === 'FieldsDiffer' || r.state === 'DatasheetDiffers');
    case 'blocked': return list.filter(r => r.state === 'Blocked');
    default: return list;
  }
}

// Only sheets this database does not have can be pulled.
export const isPullable = row => row?.state === 'New';

// "Update from source": the sheet is here and some field differs. Never a blocked code.
export const canUpdate = row =>
  row?.targetId != null && row?.state !== 'Blocked' && (row.fieldChanges?.length ?? 0) > 0;

// The difference viewer opens when the datasheet differs (another revision, or other values).
export const canCompareDatasheet = row => row?.targetId != null && row?.datasheetDiff != null;

// What the difference viewer's action does, by the kind of difference.
export const DATASHEET_ACTION = {
  OtherRevision: { id: 'adopt', label: 'Adopt source revision' },
  ValuesDiffer: { id: 'newRevision', label: 'New revision with source values' },
};

// What a pull does to the manufacturer, in a few words.
export function manufacturerText(row) {
  const p = row?.manufacturerPlan;
  switch (p?.action) {
    case 'Link': return `Linked to ${row.manufacturerCode} here`;
    case 'Create': return `${row.manufacturerCode} created here`;
    case 'Unlinked': return 'No manufacturer (source has no code)';
    case 'Blocked': return p.message || 'Manufacturer cannot be resolved';
    default: return 'No manufacturer';
  }
}

// What a pull does with the datasheet, in a few words.
export function datasheetText(row, bringValues) {
  if (row?.dsConfig == null) return 'No datasheet';
  if (!row.datasheetPullable) return `Sheet only: ${row.datasheetNote || 'its datasheet cannot come along'}`;
  const n = row.valueCount ?? 0;
  return bringValues && n > 0
    ? `${row.dsIndex} with ${n} value${n === 1 ? '' : 's'}`
    : `${row.dsIndex}${bringValues ? ' (no values)' : ', values left behind'}`;
}

// Per-status counts of a pull's results.
export function summarizePull(results) {
  const out = { pulled: 0, skipped: 0, failed: 0 };
  (results ?? []).forEach(r => { if (out[r.status] !== undefined) out[r.status] += 1; });
  return out;
}

// Drops selected ids that are no longer pullable (after a reload or a source change).
export const keepPullable = (selected, rows) => {
  const ok = new Set((rows ?? []).filter(isPullable).map(r => r.sourceId));
  return new Set([...(selected ?? [])].filter(id => ok.has(id)));
};
